import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import OrganizationalUnitLevelFilter from './filters/OrganizationalUnitLevelFilter';
import HierarchyTypeFilter from './filters/HierarchyTypeFilter';
import {HierarchyType} from '../../admin/artisticobjects/types/OrganizationalUnit';
import organizationalUnitsService from '../../admin/artisticobjects/services/organizationalUnitsService';
import {handleError} from '../../common/utils/errorHandling';
import {OrganizationalUnitsSelectionState} from '../state/OrganizationalUnitsSelectionState';

interface PlacesFiltersBarProps {
  onFilterChange: (organizationalUnitIds: string[], hierarchyType: string) => void;
  initialFilter?: string[];
  initialHierarchyType?: string;
}

const PlacesFiltersBar: React.FC<PlacesFiltersBarProps> = ({
  onFilterChange,
  initialFilter = [],
  initialHierarchyType = HierarchyType.CHURCH
}) => {
  const {t} = useTranslation();
  const [hierarchyType, setHierarchyType] = useState<string>(initialHierarchyType);
  const [state, setState] = useState<OrganizationalUnitsSelectionState | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [stateVersion, setStateVersion] = useState(0);

  useEffect(() => {
    fetchHierarchicalUnits();
  }, [hierarchyType]);

  useEffect(() => {
    if (state && initialFilter.length > 0 && !isInitialized) {
      state.setSelectedFlat(initialFilter);
      setIsInitialized(true);
      setStateVersion(v => v + 1);
    }
  }, [state, initialFilter, isInitialized]);

  useEffect(() => {
    if (!state) return;
    const resolvedIds = state.getResolvedSelection();
    onFilterChange(resolvedIds, hierarchyType);
  }, [state, stateVersion, hierarchyType]);

  const fetchHierarchicalUnits = async () => {
    handleError(async () => {
      const units = await organizationalUnitsService.getHierarchicalOrganizationalUnitsByType(hierarchyType);
      const newState = new OrganizationalUnitsSelectionState(units);
      setState(newState);
      setIsInitialized(false);
    }, t('common.errors.fetchingOrgUnits'));
  };

  const handleHierarchyTypeChange = (newType: string) => {
    setHierarchyType(newType);
  };

  const handleLevelChange = (level: number, newSelections: string[]) => {
    if (!state) return;
    state.setSelectedAtLevel(level, newSelections);
    setStateVersion(v => v + 1);
  };

  const getOptionsForLevel = (level: number) => {
    if (!state) return [];
    const availableUnits = state.getAvailableUnitsByLevel(level);

    return availableUnits.map(unit => ({
      label: unit.name,
      value: unit.id
    }));
  };

  const getLevelLabel = (level: number): string => {
    if (hierarchyType === HierarchyType.CHURCH) {
      const churchLabels = [
        t('places.filters.church.diocese'),
        t('places.filters.church.deanery'),
        t('places.filters.church.parish')
      ];
      return churchLabels[level] || t('places.filters.level', {level});
    } else {
      const administrativeLabels = [
        t('places.filters.administrative.voivodeship'),
        t('places.filters.administrative.county'),
        t('places.filters.administrative.municipality')
      ];
      return administrativeLabels[level] || t('places.filters.level', {level});
    }
  };

  if (!state) {
    return null;
  }

  return (
    <section id="filters-section" className="border-b border-gray-300 bg-white">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between flex-wrap">
          <div className="flex items-center space-x-6 text-sm">
            <div className="font-medium">{t('gallery.filters.filterBy')}</div>

            {Array.from({ length: state.getMaxLevel() + 1 }, (_, level) => {
              const options = getOptionsForLevel(level);
              if (options.length === 0) return null;

              return (
                <OrganizationalUnitLevelFilter
                  key={level}
                  levelLabel={getLevelLabel(level)}
                  selectedUnits={state.getSelectedByLevel(level)}
                  options={options}
                  onFilterChange={(selections) => handleLevelChange(level, selections)}
                />
              );
            })}

            <HierarchyTypeFilter
              hierarchyType={hierarchyType}
              onFilterChange={handleHierarchyTypeChange}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlacesFiltersBar;

